/**
 * Transfer-rate tracking for the video upload indicator.
 *
 * A naive ETA computed from total-elapsed-time is badly wrong at the start and
 * lurches whenever the connection varies, which reads as broken. This keeps a
 * short rolling window instead, so the estimate reflects the CURRENT rate and
 * settles quickly without jumping around.
 */

const WINDOW_MS = 6000;
/** Below this we don't have enough signal to claim an estimate. */
const MIN_SAMPLE_MS = 1200;

export interface TransferTracker {
  push(loaded: number, now?: number): void;
  /** Bytes per second across the window, or null when not yet meaningful. */
  ratePerSecond(): number | null;
  /** Seconds remaining for `total`, or null while still estimating. */
  etaSeconds(total: number): number | null;
  reset(): void;
}

export function createTransferTracker(): TransferTracker {
  let samples: { t: number; loaded: number }[] = [];

  const ratePerSecond = (): number | null => {
    if (samples.length < 2) return null;
    const first = samples[0];
    const last = samples[samples.length - 1];
    const seconds = (last.t - first.t) / 1000;
    const bytes = last.loaded - first.loaded;
    // Guard against a burst of samples in the same millisecond, and against a
    // stalled connection reporting zero progress (which would divide to zero
    // and produce an infinite ETA).
    if (last.t - first.t < MIN_SAMPLE_MS || bytes <= 0) return null;
    return bytes / seconds;
  };

  return {
    push(loaded, now = Date.now()) {
      samples.push({ t: now, loaded });
      // Keep the window short so the estimate tracks the current rate, but
      // never drop below two samples or the rate becomes uncomputable.
      while (samples.length > 2 && now - samples[0].t > WINDOW_MS) samples.shift();
    },
    ratePerSecond,
    etaSeconds(total) {
      const rate = ratePerSecond();
      if (!rate || !total) return null;
      const remaining = total - samples[samples.length - 1].loaded;
      if (remaining <= 0) return 0;
      return remaining / rate;
    },
    reset() {
      samples = [];
    },
  };
}

/**
 * Human phrasing for the estimate. Deliberately rounded — a countdown that
 * ticks 47s, 43s, 51s looks unreliable even when it is accurate, so we round
 * to steps a person would actually say out loud.
 */
export function formatEta(seconds: number | null): string {
  if (seconds === null) return 'estimating time…';
  if (seconds <= 0) return 'almost done';
  if (seconds < 12) return 'a few seconds left';
  if (seconds < 60) return `about ${Math.round(seconds / 10) * 10} seconds left`;
  const mins = seconds / 60;
  if (mins < 1.75) return 'about a minute left';
  if (mins < 60) return `about ${Math.round(mins)} minutes left`;
  return 'over an hour left';
}

/** Compact size for the byte counter, e.g. `12.4 MB`. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Transfer speed for the detail line, e.g. `3.4 MB/s`. */
export function formatRate(bytesPerSecond: number | null): string | null {
  if (!bytesPerSecond) return null;
  if (bytesPerSecond < 1024 * 1024) return `${(bytesPerSecond / 1024).toFixed(0)} KB/s`;
  return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
}
